<div class="login-shell">
  <aside class="login-brand">
    <h2 class="login-brand__title">Funding for South African businesses</h2>
    <ul class="login-brand__list">
      <li class="login-brand__item">
        <span class="login-brand__icon"><app-icon name="upload" [size]="20" /></span>
        <span>
          <strong>Start with your bank statements</strong>
          You don't need to know which product fits. We'll guide you to the right one.
        </span>
      </li>
      <li class="login-brand__item">
        <span class="login-brand__icon"><app-icon name="zap" [size]="20" /></span>
        <span>
          <strong>Kickstart Cash</strong>
          A lump sum of working capital repaid over a fixed term.
        </span>
      </li>
      <li class="login-brand__item">
        <span class="login-brand__icon"><app-icon name="trending-up" [size]="20" /></span>
        <span>
          <strong>Debtors Finance</strong>
          Unlock cash tied up in your unpaid invoices.
        </span>
      </li>
    </ul>
  </aside>

  <div class="login-page">
    <!-- EMAIL_NOT_FOUND error banner -->
    @if (errorState() === 'EMAIL_NOT_FOUND') {
      <div
        class="error-banner"
        data-testid="error-unknown-email"
        role="alert"
        aria-live="assertive"
      >
        <p class="error-banner__title">This email address is not linked to a CapX account.</p>
        <p class="error-banner__body">Would you like to create an account?</p>
      </div>
      <button
        mat-flat-button
        class="create-account-cta full-width"
        [routerLink]="['/auth/register']"
      >
        Create Account
      </button>
    }

    <!-- Login form card -->
    <mat-card class="login-card" appearance="outlined">
      <mat-card-content>
        <h1 class="login-card__heading">Log in to CapX Flow</h1>

        <!-- Inline error messages -->
        @if (errorState() === 'INVALID_CREDENTIALS') {
          <div
            class="inline-error"
            data-testid="error-incorrect-credentials"
            role="alert"
            aria-live="polite"
          >
            Incorrect email or password. Please try again or
            <a
              [routerLink]="['/auth/reset-password']"
              [state]="{ email: emailControl.value }"
              class="link"
              >reset your password</a
            >.
          </div>
        }
        @if (errorState() === 'EMAIL_NOT_VERIFIED') {
          <div
            class="inline-error"
            data-testid="error-email-not-verified"
            role="alert"
            aria-live="polite"
          >
            Your email address has not been verified. Please check your inbox or
            <button
              type="button"
              class="link-button"
              data-testid="login-resend-verification"
              (click)="onResendVerification()"
            >
              resend the verification email</button
            >.
          </div>
        }
        @if (errorState() === 'ACCOUNT_DISABLED') {
          <div
            class="inline-error"
            data-testid="error-account-disabled"
            role="alert"
            aria-live="polite"
          >
            Your account has been disabled. Please contact
            <a href="mailto:support@flow.capx.co.za" class="link">CapX support</a>.
          </div>
        }
        @if (errorState() === 'TOTP_INVALID') {
          <div
            class="inline-error"
            data-testid="error-totp-invalid"
            role="alert"
            aria-live="polite"
          >
            That code is incorrect or has expired. Open your authenticator app and try the current
            6-digit code.
          </div>
        }
        @if (errorState() === 'UNKNOWN') {
          <div class="inline-error" data-testid="error-unknown" role="alert" aria-live="polite">
            Something went wrong. Please try again.
          </div>
        }

        <form class="login-form" [formGroup]="form" novalidate (ngSubmit)="onSubmit()">
          <!-- Email field -->
          <div class="field-group">
            <label class="field-label" for="loginEmail">Email Address</label>
            <mat-form-field appearance="outline" class="full-width">
              <input
                matInput
                id="loginEmail"
                type="email"
                formControlName="email"
                placeholder="you@company.com"
                autocomplete="email"
              />
              <mat-error data-testid="error-email-format">
                @if (emailControl.hasError('required')) {
                  Email address is required
                } @else if (emailControl.hasError('email')) {
                  Please enter a valid email address
                }
              </mat-error>
            </mat-form-field>
          </div>

          <!-- Password field -->
          <div class="field-group">
            <label class="field-label" for="loginPassword">Password</label>
            <mat-form-field appearance="outline" class="full-width">
              <input
                matInput
                class="has-password-toggle"
                id="loginPassword"
                [type]="showPassword() ? 'text' : 'password'"
                formControlName="password"
                placeholder="••••••••"
                autocomplete="current-password"
              />
              <button
                mat-icon-button
                matSuffix
                type="button"
                (click)="togglePasswordVisibility()"
                [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
                [attr.aria-pressed]="showPassword()"
                class="password-toggle"
              >
                @if (showPassword()) {
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path
                      d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"
                    />
                    <path
                      d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"
                    />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                } @else {
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                }
              </button>
            </mat-form-field>
          </div>

          <!-- Two-factor code (revealed only when the account has 2FA enabled) -->
          @if (totpRequired()) {
            <div class="field-group" data-testid="totp-field">
              <label class="field-label" for="loginTotp">Authentication code</label>
              <mat-form-field appearance="outline" class="full-width">
                <input
                  matInput
                  id="loginTotp"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  formControlName="totpCode"
                  placeholder="123456"
                />
                <mat-hint>Enter the 6-digit code from your authenticator app.</mat-hint>
              </mat-form-field>
            </div>
          }

          <!-- Login button -->
          <button
            mat-flat-button
            type="submit"
            class="full-width"
            data-testid="login-submit"
            [disabled]="isLoading() || form.invalid"
          >
            @if (isLoading()) {
              <mat-spinner diameter="20" />
            } @else {
              Log in
            }
          </button>

          <!-- Forgot password / first time -->
          <div class="login-form__reset">
            <a
              [routerLink]="['/auth/reset-password']"
              [state]="{ email: emailControl.value }"
              class="link"
            >
              Reset password
            </a>
          </div>

          <div class="or-divider" aria-hidden="true">
            <span class="or-divider__line"></span>
            <span class="or-divider__text">or</span>
            <span class="or-divider__line"></span>
          </div>

          <!-- Create account button -->
          <button
            mat-stroked-button
            type="button"
            class="full-width"
            [routerLink]="['/auth/register']"
          >
            Create new account
          </button>
        </form>
      </mat-card-content>
    </mat-card>
  </div>
</div>
